import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCalendar, FiMapPin, FiArrowRight, FiClock } from "react-icons/fi";
import { eventAPI } from "../../services/api";
import { formatDate, resolveImageUrl } from "../../utils/helpers";
import { useDataRefresh } from "../../hooks/useDataRefresh";
import useScrollAnimation from "../../hooks/useScrollAnimation";

const fallbackEvents = [
  { _id: "1", title: "Annual Sports Day 2025",
    description: "Join us for a day of exciting sports competitions, team activities, and athletic excellence celebrating student talent.",
    date: "2025-03-15", location: "School Sports Complex", category: "Sports" },
  { _id: "2", title: "Science Exhibition & Fair",
    description: "Students showcase innovative science projects and experiments in our annual science exhibition open to parents and guests.",
    date: "2025-03-22", location: "School Auditorium", category: "Academic" },
  { _id: "3", title: "Parent-Teacher Meeting",
    description: "Quarterly meeting for parents to discuss academic progress, student development, and upcoming school activities.",
    date: "2025-04-05", location: "School Campus", category: "Meeting" },
];

const categoryColors = {
  Sports:   "bg-[#2563EB] text-white",
  Academic: "bg-[#1e40af] text-white",
  Meeting:  "bg-[#0EA5E9] text-white",
  Cultural: "bg-[#1D4ED8] text-white",
  Other:    "bg-slate-500 text-white",
};

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function NewsEventsSection() {
  const { ref, isVisible } = useScrollAnimation();
  const { data: events = [] } = useDataRefresh(
    async () => {
      try {
        const res = await eventAPI.getAll();
        const data = res.data?.data?.events || [];
        return data.length > 0 ? data.slice(0, 3) : fallbackEvents;
      } catch { return fallbackEvents; }
    },
    "events", { pollInterval: 10000 },
  );

  return (
    <section className="py-20 lg:py-32 bg-[#F0F4FF]">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
        <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={isVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-16 gap-8">
          <div>
            <SectionLabel text="Stay Updated" />
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0F172A] leading-tight">
              News &amp; Upcoming <span className="text-[#2563EB]">Events</span>
            </h2>
          </div>
          <Link to="/news-events"
            className="inline-flex items-center gap-3 px-6 py-3 bg-[#2563EB] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors shrink-0 rounded-xl shadow-sm hover:shadow-md">
            View All Events <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {events.map((event, idx) => (
            <motion.article key={event._id || idx}
              initial={{ opacity: 0, y: 30 }} animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-blue-100 overflow-hidden flex flex-col hover:-translate-y-1 transition-all duration-300 group">
              <div className="h-52 bg-blue-50 relative overflow-hidden">
                {event.image?.url ? (
                  <img src={resolveImageUrl(event.image.url)} alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#F0F4FF]">
                    <FiCalendar className="w-14 h-14 text-blue-200" />
                  </div>
                )}
                <div className="absolute top-4 left-4 z-10">
                  <span className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-full ${categoryColors[event.category] || categoryColors.Other}`}>
                    {event.category || "Event"}
                  </span>
                </div>
                <div className="absolute bottom-0 right-0 bg-[#2563EB] text-white px-5 py-3 text-center rounded-tl-xl">
                  <div className="font-black text-xl font-mono leading-none">
                    {event.date ? new Date(event.date).getDate() : ""}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5">
                    {event.date ? new Date(event.date).toLocaleString("default", { month: "short" }) : ""}
                  </div>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-bold text-[#0F172A] text-lg mb-3 line-clamp-2 group-hover:text-[#2563EB] transition-colors">{event.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">{event.description}</p>
                <div className="space-y-2 pt-4 border-t border-blue-50">
                  {event.date && (
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                      <FiClock className="w-3.5 h-3.5 text-[#2563EB]" />
                      {formatDate(event.date)}
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                      <FiMapPin className="w-3.5 h-3.5 text-[#2563EB]" />
                      {event.location}
                    </div>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
